import React, { useEffect, useRef, useState } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  makeStyles
} from '@material-ui/core';
import * as L from 'leaflet';
import { ImageOverlay, Map, Marker, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import MarkerURL from 'images/map_marker.svg';

const MarkerIcon = new L.Icon({
  iconUrl: MarkerURL,
  iconRetinaUrl: MarkerURL,
  iconAnchor: new L.Point(21, 53),
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null,
  iconSize: new L.Point(42, 58)
});

const useStyles = makeStyles(theme => ({
  companiesMobile: {
    backgroundColor: '#CCE1E9',
    padding: '24px 24px 8px',
    '@media screen and (min-width: 450px)': {
      padding: '24px 32px 8px'
    },
    '@media screen and (min-width: 840px)': {
      display: 'none'
    },
    '& label': {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: 700,
      textTransform: 'uppercase',
      color: '#555 !important',
      top: -4
    },
    '& svg': {
      display: 'none'
    },
    '& .MuiInputBase-root': {
      backgroundColor: '#E4EEF1 !important',
      '&::before, &::after': {
        display: 'none'
      }
    },
    '& .MuiSelect-root': {
      padding: '16px 32px 16px 16px',
      '&::after': {
        content: '""',
        borderTop: '14px solid #00577a',
        borderLeft: '11px solid transparent',
        borderRight: '11px solid transparent',
        position: 'absolute',
        top: 19,
        right: 16,
      }
    }
  },
  companies: {
    display: 'none',
    flexWrap: 'wrap',
    justifyContent: 'center',
    backgroundColor: '#CCE1E9',
    padding: '0 72px',
    '@media screen and (min-width: 840px)': {
      display: 'flex'
    },
    '&::after': {
      content: '""',
      width: '100%',
      height: 1,
      backgroundColor: '#BBD6E1',
      marginTop: 24
    }
  },
  company: {
    margin: '24px 6px 0',
    background: 'none',
    color: '#00577a',
    lineHeight: 1,
    padding: '0 18px',
    fontWeight: 800,
    minHeight: 40,
    fontFamily: 'proxima-nova',
    '&:focus': {
      outline: 'none'
    }
  },
  activeCompany: {
    backgroundColor: "#914d9f",
    color: 'white',
    borderRadius: 26,
    '& span': {
      display: 'inline-block',
      width: 24,
      height: 24,
      transform: 'rotate(45deg)',
      position: 'relative',
      marginLeft: 6,
      '&::before, &::after': {
        content: '""',
        width: 24,
        height: 6,
        borderRadius: 3,
        backgroundColor: 'white',
        position: 'absolute'
      },
      '&::before': {
        width: 24,
        height: 6,
        top: 9,
        left: 0
      },
      '&::after': {
        width: 6,
        height: 24,
        top: 0,
        left: 9
      }
    },
    '&.blue': {
      backgroundColor: '#00577a'
    },
    '&.grey': {
      backgroundColor: '#4f5050'
    }
  },
  map: {
    position: 'relative',
    '&::before': {
      content: '""',
      display: 'block',
      width: '100%',
      paddingTop: '55.421%'
    },
    '& > div': {
      position: 'absolute !important',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: '#CCE1E9'
    }
  }
}));

const LocationsMap = props => {
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [currentFilter, setCurrentFilter] = useState(null);
  let map = useRef(null);

  const classes = useStyles();

  const bounds = L.latLngBounds([[0, 0], [772, 1393]]);

  useEffect(() => {
    fetch(`/companies.json`)
      .then(response => response.json())
      .then(data => {
        data.find(c => c.title.match(/Wa/)).color = 'blue';
        data.find(c => c.title.match(/So/)).color = 'grey';
        data.find(c => c.title.match(/Ela/)).color = 'blue';

        // dirty ordering logic since they wanted a specific order
        let loadedCompanies = [
          data.find(c => c.title.match(/Wa/)),
          data.find(c => c.title.match(/Un/)),
          data.find(c => c.title.match(/So/)),
          data.find(c => c.title.match(/Ela/)),
          data.find(c => c.title.match(/Liq/))
        ];

        const otherCompanies = data.filter(c => !c.title.match(/Wa|Un|So|Ela|Liq/));
        loadedCompanies = loadedCompanies.concat(otherCompanies);

        setCompanies(loadedCompanies);
      })
      .catch(error => {
        console.error(error);
      });

    fetch(`/locations.json`)
      .then(response => response.json())
      .then(data => {
        setLocations(data);
        setFilteredLocations(data);
      })
      .catch(error => {
        console.error(error);
      });
  }, []);

  useEffect(() => {
    if (map) {
      const mapInstance = map.current.leafletElement,
        initialZoom = setZoom(),
        initialCenter = bounds.getCenter();

      mapInstance.setView(initialCenter, initialZoom);
    }
  }, [map]);

  window.addEventListener('resize', () => {
    if (map) {
      const mapInstance = map.current.leafletElement,
        initialZoom = setZoom(),
        initialCenter = bounds.getCenter();

      const currentZoom = mapInstance.getZoom();
      if (currentZoom !== initialZoom) {
        mapInstance.setView(initialCenter, initialZoom);
        mapInstance.setView(initialCenter, initialZoom);
      }
    }
  });

  const setZoom = () => {
    const zoom = window.innerWidth < 500
      ? -2
      : window.innerWidth < 690
      ? -1.5
      : window.innerWidth < 800
      ? -1
      : window.innerWidth < 950
      ? -0.75
      : window.innerWidth < 1120
      ? -0.5
      : window.innerWidth < 1340
      ? -0.25
      : window.innerWidth < 1800
      ? 0
      : window.innerWidth < 2300
      ? 0.25
      : 0.75;

    return zoom;
  }

  const filterLocations = company => {
    if (currentFilter === company.id) {
      setCurrentFilter(null);
      setFilteredLocations([...locations]);
    } else {
      setCurrentFilter(company.id);
      setFilteredLocations(
        [...locations].map(l => {
          if (l.companies.find(c => c.value === company.id)) {
            return l;
          }
        }).filter(n => n)
      );
    }
  }

  const handleSelectChange = event => {
    if (!event.target.value) {
      setCurrentFilter(null);
      setFilteredLocations([...locations]);
    } else {
      const selectedCompany = companies.find(c => c.id === event.target.value);
      filterLocations(selectedCompany);
    }
  }

  return (
    <div>
      <div className={classes.companiesMobile}>
        <FormControl
          className={classes.formControl}
          fullWidth
        >
          <InputLabel shrink id="companies-label">
            Show Locations For
          </InputLabel>
          <Select
            labelId="companies-label"
            value={currentFilter || ''}
            onChange={handleSelectChange}
            displayEmpty
            className={classes.selectEmpty}
          >
            <MenuItem value="">
              All Companies
            </MenuItem>

            {companies.map(company =>
              <MenuItem
                key={company.id}
                value={company.id}
              >
                {company.title}
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </div>

      <div className={classes.companies}>
        {companies.map(company =>
          <button
            key={company.id}
            className={`${classes.company} ${currentFilter === company.id ? classes.activeCompany : ''} ${company.color}`}
            onClick={filterLocations.bind(this, company)}
          >
            {company.title}
            <span></span>
          </button>
        )}
      </div>

      <div className={classes.map}>
        <Map
          attributionControl={false}
          crs={L.CRS.Simple}
          minZoom={-2}
          maxZoom={4}
          zoom={0}
          bounds={bounds}
          maxBounds={bounds}
          maxBoundsViscosity={0.6}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          zoomSnap={0.25}
          tap={false}
          ref={map}
        >
          <ImageOverlay
            bounds={bounds}
            url="/assets/map.svg"
          />

          {filteredLocations.map((location, index) =>
            <React.Fragment key={index}>
              {location.x_position && (
                <Marker
                  position={[location.x_position, location.y_position]}
                  icon={MarkerIcon}
                >
                  <Tooltip
                    direction="top"
                    offset={[0, -40]}
                  >{location.name}</Tooltip>
                </Marker>
              )}
            </React.Fragment>
          )}
        </Map>
      </div>
    </div>
  )
}

export default LocationsMap